import "./Accounts.css";
import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import PrimaryButton from "../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import FormModal from "../../../Components/Molecules/FormModal/FormModal";
import { BarLoader } from "react-spinners";
import { BaseURL } from "../../../Hooks/URL";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import TextFieldInput from "../../../Components/Atoms/Inputs/TextField/TextFieldInput";
import { styled } from "@mui/material/styles";
import { images } from "../../../Assets/assetsHelper";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import FilterListIcon from "@mui/icons-material/FilterList";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import { Pagination } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&:first-child": {
    borderRadius: "5px 0 0 0",
  },
  "&:last-child": {
    borderRadius: "0 5px 0 0",
  },
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--primary)",
    color: theme.palette.common.white,
    fontSize: "15px",
  },
}));

const columns = [
  {
    id: "code",
    label: "Code",
    minWidth: 100,
  },
  {
    id: "admin",
    label: "Admin Name",
    minWidth: 150,
  },
  {
    id: "teamsCount",
    label: "Teams Count",
    minWidth: 150,
  },
  {
    id: "type",
    label: "Account Type",
    minWidth: 150,
  },
  {
    id: "organization",
    label: "Organization",
    minWidth: 150,
  },
  { id: "promoCode", label: "Promo Code", minWidth: 100 },
  { id: "referralSource", label: "Come From", minWidth: 100 },
  { id: "paid", label: "Payment Status", minWidth: 100 },
  { id: "accountPrice", label: "Price", minWidth: 100 },
  {
    id: "registeredAt",
    label: "Registration Date",
    minWidth: 100,
  },
];

function Accounts({
  page,
  rowsPerPage,
  handleChangeRowsPerPage,
  searchData,
  search,
  setSearchData,
  setSearch,
  accounts,
  navigate,
  handleChangePage,
  loading,
  pageCount,
  setPage,
  sortBy,
  sortOrder,
  filters,
  handleSortChange,
  handleSortOrderChange,
  handleFilterChange,
  organizationList,
  accountsNumber,
  referralSourceList,
  getFilterQueryString,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(!isMobile);

  return (
    <div className="mainContainer">
      <div className="cont">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Total Accounts: {accountsNumber}</h2>
          <a
            href={`${BaseURL}/admin/dashboard/accounts/excel?${
              sortBy ? `&sortBy=${sortBy}` : ""
            }${sortOrder ? `&sortDesc=${sortOrder}` : ""}${
              getFilterQueryString(filters)
                ? `&${getFilterQueryString(filters)}`
                : ""
            }${search ? `&search=${search}` : ""}&timeZone=${
              Intl.DateTimeFormat().resolvedOptions().timeZone
            }`}
            target="_blank"
          >
            <PrimaryButton
              classBtn={"center editButton"}
              text={"Download Excel"}
              style={{
                textTransform: "capitalize",
                borderRadius: "5px",
                color: "var(--text-secondary)",
                backgroundColor: "var(--text-primary)",
                height: "40px",
                padding: "0 30px",
                fontSize: "15px",
              }}
            />
          </a>
        </Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: "var(--primary)",
                color: "white",
                "&:hover": {
                  backgroundColor: "#8a0505",
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          )}
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextFieldInput
                searchIcon={true}
                Required={false}
                type="text"
                placeholder={"Search"}
                className="SearchBar"
                name={"search"}
                change={(e) => setSearchData(e.target.value)}
                newValue={searchData}
                searchAction={() => {
                  if (search != searchData) {
                    setSearch(searchData);
                    setPage(0);
                  }
                }}
                keyDown={(e) => {
                  if (e.keyCode === 13)
                    if (search != searchData) {
                      setSearch(searchData);
                      setPage(0);
                    }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Come From</InputLabel>
                <Select
                  value={filters.referralSource}
                  label="Come From"
                  onChange={handleFilterChange}
                  name="referralSource"
                  defaultValue={filters.referralSource}
                >
                  <MenuItem value="">All</MenuItem>
                  {referralSourceList?.map((referralSource) => (
                    <MenuItem
                      key={referralSource.referralSource}
                      value={referralSource.referralSource}
                    >
                      {referralSource.referralSource}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortChange}
                  defaultValue={sortBy}
                >
                  {[
                    { id: "registeredAt", label: "Registration Date" },
                    { id: "accountPriceCents", label: "Price" },
                  ].map((column) => (
                    <MenuItem key={column.id} value={column.id}>
                      {column.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={handleSortOrderChange}
                >
                  <MenuItem value={true}>
                    {sortBy == "registeredAt"
                      ? "Newest to Oldest"
                      : "Highest to Lowest"}
                  </MenuItem>
                  <MenuItem value={false}>
                    {sortBy == "registeredAt"
                      ? "Oldest to Newest"
                      : "Lowest to Highest"}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={filters.paid}
                  label="Payment Status"
                  name="paid"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Paid</MenuItem>
                  <MenuItem value={0}>Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Has Teams</InputLabel>
                <Select
                  value={filters.hasTeams}
                  label="Has Teams"
                  name="hasTeams"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Account Type"
                  name="type"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="organization">Organization</MenuItem>
                  <MenuItem value="personal">Personal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={filters.organizationId}
                  label="Organization"
                  name="organizationId"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  {organizationList?.map((organization) => (
                    <MenuItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>

        <Paper
          className="tableCont"
          sx={{ width: "fit-content", minWidth: "100%" }}
        >
          {accounts && !loading ? (
            <>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <StyledTableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            minWidth: column.minWidth,
                          }}
                        >
                          {column.label}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accounts?.map((account, i) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={account.id}
                          onClick={() => {
                            navigate(`/accounts/${account.id}`);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {columns.map((column) => {
                            const value = account[column.id];
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                style={{ padding: "10px" }}
                              >
                                {column.id == "accountPrice"
                                  ? `${(value?.amountCents / 100).toFixed(2)} ${
                                      value?.currency
                                    }`
                                  : column.id == "admin" ||
                                    column.id == "organization"
                                  ? value?.name ?? "-"
                                  : column.id == "paid"
                                  ? value
                                    ? "Paid"
                                    : "Unpaid"
                                  : column.id == "registeredAt"
                                  ? `${new Date(
                                      value
                                    )?.toLocaleDateString()} / ${new Date(
                                      value
                                    )?.toLocaleTimeString()}`
                                  : value ?? "-"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {!accounts?.length ? (
                  <h3 style={{ textAlign: "center", margin: "50px" }}>
                    No Accounts Added Yet!!
                  </h3>
                ) : (
                  ""
                )}
              </TableContainer>
              <Pagination
                count={pageCount}
                page={page + 1}
                onChange={handleChangePage}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 20px`",
                  padding: "20px",
                  color: "var(--primary)",
                  "& .Mui-selected": {
                    backgroundColor: "var(--primary)",
                    color: "white",
                  },
                }}
              />
              {/* <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={pageCount * rowsPerPage}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              /> */}
            </>
          ) : (
            <div className="loadingBox">
              <BarLoader color="var(--primary)" />
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
}

export default Accounts;
